import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import {
  createFreehandDrawing as createFreehandDrawingApi,
  deleteFreehandDrawing as deleteFreehandDrawingApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
} from "./igpService";
import {
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * Converts an array of coordinate points to an SVG path data string
 * @param {Array<{x: number, y: number}>} points - Array of points
 * @returns {string} SVG path data (d attribute value)
 */
const pointsToSvgPath = (points) => {
  if (!points || points.length === 0) return "";

  let pathData = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 1; i < points.length; i++) {
    pathData += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
  }

  return pathData;
};

/**
 * Generates complete SVG markup from drawing stroke data with fixed color and width
 * @param {Array<{x: number, y: number}>} points - Array of points
 * @returns {string} Complete SVG XML
 */
const generateSvgMarkup = (points) => {
  const pathData = pointsToSvgPath(points);
  const color = "#ff0000"; // Fixed color: red
  const lineWidth = 1; // Fixed width
  const pageWidth = 612;
  const pageHeight = 792;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pageWidth} ${pageHeight}" width="${pageWidth}" height="${pageHeight}">
  <path d="${pathData}" stroke="${color}" stroke-width="${lineWidth}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
};

/**
 * Encodes SVG markup to base64
 * @param {string} svgMarkup - Complete SVG XML string
 * @returns {string} Base64 encoded SVG
 */
const encodeSvgToBase64 = (svgMarkup) => {
  return btoa(unescape(encodeURIComponent(svgMarkup)));
};

/**
 * Decodes base64 SVG to SVG markup
 * @param {string} base64Data - Base64 encoded SVG
 * @returns {string} SVG markup
 */
export const decodeBase64ToSvg = (base64Data) => {
  try {
    return decodeURIComponent(escape(atob(base64Data)));
  } catch (error) {
    console.error("Error decoding base64 SVG:", error);
    return "";
  }
};

/**
 * Parses SVG path data string and extracts coordinate points
 * @param {string} pathData - SVG path data (d attribute value)
 * @returns {Array<{x: number, y: number}>} Array of points
 */
export const svgPathToPoints = (pathData) => {
  const points = [];

  if (!pathData) return points;

  const regex = /[ML]\s*([\d.]+)\s+([\d.]+)/g;
  let match;

  while ((match = regex.exec(pathData)) !== null) {
    points.push({
      x: parseFloat(match[1]),
      y: parseFloat(match[2]),
    });
  }

  return points;
};

/**
 * Converts API format drawing to local stroke object
 * @param {Object} apiDrawing - Drawing from API { id, page_number, drawing_data, timestamp }
 * @returns {Object} Stroke object { id, points, page_number }
 */
export const apiFormatToStroke = (apiDrawing) => {
  const svgMarkup = decodeBase64ToSvg(apiDrawing.drawing_data);
  const pathData = svgMarkup.match(/d="([^"]+)"/)?.[1] || "";
  const points = svgPathToPoints(pathData);

  return {
    id: apiDrawing.id,
    points,
    page_number: apiDrawing.page_number,
  };
};

/**
 * Refreshes drawings from API and updates the store
 * @returns {Promise<Array>} The drawings from the API
 */
export async function refreshDrawingsFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, [
      "freehand_drawing",
    ]);
    const drawings = accessData?.freehand_drawing?.data || [];
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("freehand_drawing", drawings);
    return drawings;
  } catch (err) {
    console.error("Error refreshing drawings from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

/**
 * Saves a freehand drawing to API
 * Handles format conversion (points → SVG → base64) and auth
 * @param {Object} drawingData - { page_number, points }
 * @returns {Promise<Object>} The API response
 */
export async function saveFreehandDrawing(drawingData) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    const svgMarkup = generateSvgMarkup(drawingData.points);
    const base64Data = encodeSvgToBase64(svgMarkup);
    const id =
      drawingData.id ||
      `draw_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    await createFreehandDrawingApi(grantId, rstToken, {
      id,
      page_number: drawingData.page_number,
      drawing_data: base64Data,
      timestamp: new Date().toISOString(),
    });
    return await refreshDrawingsFromApi();
  } catch (error) {
    console.error("Error saving drawing:", error);
    if (error.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.saveDrawing"));
    }
    throw error;
  }
}

/**
 * Deletes a freehand drawing from API
 * @param {string} drawingId - The ID of the drawing to delete
 * @returns {Promise<Object>} The API response
 */
export async function removeDrawingStrokeById(drawingId) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteFreehandDrawingApi(grantId, rstToken, drawingId);
    return await refreshDrawingsFromApi();
  } catch (error) {
    console.error("Error deleting drawing:", error);
    if (error.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteDrawing"));
    }
    throw error;
  }
}
